import React, { FC, useEffect, useRef, useState } from 'react';

import useTranslation from '@app/hooks/use-translation';

import { v4 } from 'uuid';

import { TableFilter } from '../table/table.type';

import Empty from '../ui/empty';

import TableSkeleton from '../table/table-skeleton';

import Icon from '../ui/icons';

import { TableProps } from './table.types';
import { Columns } from './blocks/columns';
import { Rows } from './blocks/rows';

const RubberTable: FC<TableProps> = (props) => {
  const {
    dataSource = [],
    columns,
    onFilter,
    isEmpty,
    isPending,
    skeletons,
    truncate = true,
    onChangeOrder,
    withoutCols = false,
    block_id,
    specialPropForSearchTable = false,
  } = props;
  const [filterData, setFilterData] = useState<TableFilter>();
  // const isDraggable = !!onChangeOrder;
  const [data, setData] = useState(dataSource);
  const { t } = useTranslation('components.empty');
  function handlerResetFilters() {
    setFilterData({});
    onFilter?.({});
  }
  function handleOnFilter(titleIndex: string, isFilter: boolean | undefined) {
    if (!isFilter) return;
    if (filterData && filterData[titleIndex] === 1) {
      handlerResetFilters();
      return;
    }
    const updatedFilter: TableFilter = {
      [titleIndex]: filterData?.[titleIndex] !== undefined ? 1 : -1,
    };
    setFilterData(updatedFilter);
    onFilter?.(updatedFilter);
  }

  const dragEnabled = useRef(false);
  const onMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    dragEnabled.current = !!target.closest('.draggable-item');
  };
  const dragEventMArkup = (
    <div onMouseDown={onMouseDown} className="ml-[-7px]  cursor-pointer draggable-item">
      <Icon size={10} name="GridIcon" className="text-3color" />
    </div>
  );

  useEffect(() => {
    setData(dataSource);
  }, [dataSource]);

  const [dragAndDrop, setDragAndDrop] = useState<{
    draggedFrom: null | number;
    draggedTo: null | number;
    isDragging: boolean;
    originalOrder: TableProps['dataSource'];
    updatedOrder: TableProps['dataSource'];
    block_target?: string;
  }>({
    draggedFrom: null,
    draggedTo: null,
    isDragging: false,
    originalOrder: [],
    updatedOrder: [],
  });

  const handleDragStart = (event: React.DragEvent<HTMLTableRowElement>, index: number) => {
    if (!dragEnabled.current) {
      event.preventDefault();
      return;
    }
    setDragAndDrop({
      ...dragAndDrop,
      draggedFrom: index,
      isDragging: true,
      originalOrder: data,
      block_target: block_id,
    });

    event.dataTransfer.setData('text/html', '');
  };

  const handleDragOver = (event: React.DragEvent<HTMLTableRowElement>, index: number) => {
    event.preventDefault();
    if (dragAndDrop.block_target !== block_id) return;
    if (index == dragAndDrop.draggedTo) return;

    let newList = dragAndDrop.originalOrder;

    const draggedFrom = dragAndDrop.draggedFrom;

    const draggedTo = index;

    const itemDragged = newList[draggedFrom as number];
    const remainingItems = newList.filter((item, index) => index !== draggedFrom);

    newList = [
      ...remainingItems.slice(0, draggedTo),
      itemDragged,
      ...remainingItems.slice(draggedTo),
    ];

    if (draggedTo !== dragAndDrop.draggedTo) {
      setData(newList);

      setDragAndDrop({
        ...dragAndDrop,
        updatedOrder: newList,
        draggedTo: draggedTo,
      });
    }
  };
  function arraysAreEqual<T>(a: T[], b: T[]): boolean {
    if (a.length !== b.length) return false;
    return a.every((item, index) => item === b[index]);
  }
  const onDrop = () => {
    // setData(dragAndDrop.updatedOrder);
    setDragAndDrop({
      ...dragAndDrop,
      draggedFrom: null,
      draggedTo: null,
      isDragging: false,
      block_target: undefined,
    });
    if (!arraysAreEqual(dragAndDrop.updatedOrder, dragAndDrop.originalOrder))
      dragAndDrop.updatedOrder &&
        onChangeOrder?.(
          dragAndDrop.updatedOrder.map((data, index) => ({
            order: index,
            id: String(data?.id),
          })),
        );
  };

  if (isPending) return <TableSkeleton withoutHead count={skeletons || data.length} />;
  const renderColumns = (
    <thead className="!bg-basic_app_bg">
      <tr className="sticky z-50 top-0 min-w-[80px] truncate !bg-basic_app_bg  uppercase ">
        {columns?.map(({ title, filter, index, tag, truncate }) => {
          return (
            <Columns
              index={index}
              title={title}
              filterData={filterData}
              handleOnFilter={handleOnFilter}
              key={index + v4()}
              tag={tag}
              filter={filter}
              truncate={truncate}
            />
          );
        })}
      </tr>
    </thead>
  );

  const renderCells = (
    <tbody className="relative z-30 w-full">
      {data
        .map((i) => ({ ...i, dragDrop: dragEventMArkup }))
        .map((row, index) => {
          return (
            <tr
              key={index}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={onDrop}
              onDragOver={(event) => handleDragOver(event, index)}
              // onDragLeave={handleDragLeave}
              className={`12 w-full transition-opacity  ${
                dragAndDrop.draggedTo === index
                  ? 'opacity-40 duration-800'
                  : 'opacity-100 duration-500'
              }`}
            >
              {columns?.map((column, colIndex) => {
                return (
                  <Rows
                    specialPropForSearchTable={specialPropForSearchTable}
                    row={row}
                    column={column}
                    truncate={typeof column?.truncate === 'undefined' ? truncate : column.truncate}
                    key={`${index}_${colIndex}`}
                  />
                );
              })}
            </tr>
          );
        })}
    </tbody>
  );

  return (
    <div className={'w-full h-full z-[1] relative'}>
      {dataSource.length !== 0 ? (
        <table
          style={{
            borderSpacing: '0px 2px',
            borderCollapse: 'separate',
            tableLayout: 'fixed',
            width: '100%',
          }}
          align="left"
        >
          <colgroup>
            {columns?.map((e) => {
              return <col key={e.index} width={e.maxWidth || e.minWidth || '100%'} />;
            })}
          </colgroup>
          {!withoutCols && renderColumns}
          {renderCells}
        </table>
      ) : (
        <Empty title={isEmpty || t('title')} />
      )}
    </div>
  );
};

export default RubberTable;
